import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence, useInView } from "framer-motion"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { Factory, MapPin, Globe2, TrendingUp } from "lucide-react"

interface HubNode {
  id: string
  name: string
  suppliers: number
  sector: string
  coords: [number, number] // [latitude, longitude]
}

function AnimatedCounter({ targetValue, suffix = "+" }: { targetValue: number; suffix?: string }) {
  const [count, setCount] = useState(0)
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-30px" })

  useEffect(() => {
    if (!isInView) return
    let start = 0
    const duration = 1800
    const steps = 40
    const increment = targetValue / steps
    const stepTime = duration / steps

    const timer = setInterval(() => {
      start += increment
      if (start >= targetValue) {
        setCount(targetValue)
        clearInterval(timer)
      } else {
        setCount(Math.floor(start))
      }
    }, stepTime)

    return () => clearInterval(timer)
  }, [isInView, targetValue])

  return (
    <span ref={ref}>
      {count}
      {suffix}
    </span>
  )
}

const MANUFACTURING_HUBS: HubNode[] = [
  { id: "delhi", name: "Delhi NCR", suppliers: 176, sector: "Textiles, CleanTech & Packaging", coords: [28.7041, 77.1025] },
  { id: "surat", name: "Surat", suppliers: 142, sector: "Synthetics & Solar Components", coords: [21.1702, 72.8311] },
  { id: "pune", name: "Pune", suppliers: 43, sector: "Auto Parts & Precision Forging", coords: [18.5204, 73.8567] },
  { id: "kolkata", name: "Kolkata", suppliers: 136, sector: "Jute, Metals & Heavy Engineering", coords: [22.5726, 88.3639] },
  { id: "tirupur", name: "Tirupur", suppliers: 209, sector: "Cotton Knitwear & Export Apparel", coords: [11.1085, 77.3411] },
]

const STAT_CARDS = [
  {
    id: "suppliers",
    targetNumber: 1500,
    suffix: "+",
    title: "Verified Suppliers",
    desc: "Pre-vetted for quality and consistency",
    icon: Factory,
    iconBg: "bg-primary/10 text-primary border border-primary/20",
    borderHover: "hover:border-primary/40 hover:shadow-primary/10",
  },
  {
    id: "cities",
    targetNumber: 25,
    suffix: "+",
    title: "Cities Covered",
    desc: "Extensive supplier network across key sectors",
    icon: MapPin,
    iconBg: "bg-primary/10 text-primary border border-primary/20",
    borderHover: "hover:border-primary/40 hover:shadow-primary/10",
  },
  {
    id: "countries",
    targetNumber: 8,
    suffix: "+",
    title: "Countries Served",
    desc: "Enabling seamless cross-border sourcing",
    icon: Globe2,
    iconBg: "bg-surface-container text-primary border border-outline-variant/60",
    borderHover: "hover:border-primary/40 hover:shadow-primary/10",
  },
  {
    id: "buyers",
    targetNumber: 500,
    suffix: "+",
    title: "Global Buyers",
    desc: "Growing through trusted partnerships",
    icon: TrendingUp,
    iconBg: "bg-surface-container text-primary border border-outline-variant/60",
    borderHover: "hover:border-primary/40 hover:shadow-primary/10",
  },
]

export function PanIndiaNetwork() {
  const [activeHub, setActiveHub] = useState<HubNode | null>(null)
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)

  useEffect(() => {
    if (!mapContainerRef.current) return
    if (mapInstanceRef.current) return // Prevent multiple initializations

    // Initialize Leaflet map centered over India
    const map = L.map(mapContainerRef.current, {
      center: [22.0, 79.2],
      zoom: 4.3,
      minZoom: 3.8,
      maxZoom: 7,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: true,
      doubleClickZoom: false,
    })

    // Clean, English-only World Light Gray Basemap (free, no API key required)
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 16,
        attribution: "Tiles &copy; Esri",
      }
    ).addTo(map)

    // English-only labels reference layer
    L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
      {
        maxZoom: 16,
      }
    ).addTo(map)

    // Add clean, theme-aligned markers for each manufacturing hub
    MANUFACTURING_HUBS.forEach((hub) => {
      const customIcon = L.divIcon({
        className: "custom-hub-marker",
        html: `
          <div class="relative flex flex-col items-center group cursor-pointer" style="transform: translate(-50%, -50%);">
            <!-- Theme Primary Core Node -->
            <div class="w-3.5 h-3.5 rounded-full bg-primary border-2 border-white shadow-sm transition-transform duration-150 hover:scale-125 hover:bg-primary-container"></div>
            <!-- City Label matching Theme Tokens -->
            <div class="mt-1 whitespace-nowrap text-center bg-white/95 backdrop-blur-xs px-1.5 py-0.5 rounded-md border border-outline-variant/60 shadow-2xs">
              <span class="block text-[9px] font-bold text-on-surface leading-none">${hub.name}</span>
              <span class="block text-[7.5px] font-semibold text-secondary font-mono leading-none mt-0.5">${hub.suppliers} suppliers</span>
            </div>
          </div>
        `,
        iconSize: [0, 0],
        iconAnchor: [0, 0],
      })

      const marker = L.marker(hub.coords, { icon: customIcon }).addTo(map)

      marker.on("mouseover", () => {
        setActiveHub(hub)
      })
      marker.on("mouseout", () => {
        setActiveHub(null)
      })
      marker.on("click", () => {
        setActiveHub(hub)
      })
    })

    mapInstanceRef.current = map

    return () => {
      map.remove()
      mapInstanceRef.current = null
    }
  }, [])

  return (
    <section id="pan-india-network" className="py-16 md:py-24 bg-surface relative overflow-hidden">
      
      {/* Ambient Theme Highlight */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-primary/5 rounded-full blur-[130px] pointer-events-none" />

      <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-5xl mx-auto mb-12 space-y-2">
          <motion.h2 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-sans text-2xl sm:text-3xl md:text-4xl font-black text-on-surface tracking-tight leading-tight max-w-4xl mx-auto"
          >
            Trusted Indian Suppliers + Global Delivery Capacity
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-secondary text-sm md:text-base font-normal max-w-2xl mx-auto"
          >
            Connected to India's key manufacturing hubs, delivering global expertise
          </motion.p>
        </div>

        {/* Main Theme-Cohesive Container Card */}
        <motion.div 
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="bg-gradient-to-br from-surface-container-low via-surface to-surface-container border border-outline-variant/60 rounded-3xl p-6 md:p-8 shadow-md shadow-primary/5 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
        >
          
          {/* Left Column: Leaflet India Map with Scale-Up Hover Effect (5 cols) */}
          <div className="lg:col-span-5 bg-white rounded-2xl p-3 md:p-4 border border-outline-variant/60 shadow-xs relative overflow-hidden flex flex-col justify-between h-[390px] md:h-[430px] select-none group/map transition-all duration-300 hover:scale-[1.015] hover:shadow-lg hover:shadow-primary/5 hover:border-primary/40">
            
            {/* Leaflet Map Canvas with Smooth Scale-Up on Hover */}
            <div className="relative w-full flex-grow rounded-xl overflow-hidden border border-outline-variant/40 z-0">
              <div ref={mapContainerRef} className="w-full h-full transition-transform duration-500 ease-out group-hover/map:scale-105 origin-center" />

              {/* Floating Tooltip Info on Map Hover */}
              <AnimatePresence>
                {activeHub && (
                  <motion.div
                    initial={{ opacity: 0, y: -4, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-3 right-3 bg-on-surface text-white p-3 rounded-xl shadow-lg border border-primary/30 text-left pointer-events-none z-[1000] min-w-[150px]"
                  >
                    <p className="text-xs font-bold text-white flex items-center justify-between gap-2">
                      <span>{activeHub.name}</span>
                      <span className="text-[#ff4d5a] font-mono font-bold text-[11px]">
                        {activeHub.suppliers} suppliers
                      </span>
                    </p>
                    <p className="text-[10px] text-surface-container-highest mt-0.5 font-medium leading-tight">
                      {activeHub.sector}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

          </div>

          {/* Right Column: Title, Description & 4 Interactive Metric Cards (7 cols) */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            
            <div className="space-y-1">
              <h3 className="font-sans text-base md:text-lg lg:text-xl text-slate-700 text-on-surface tracking-tight">
                Pan-India Manufacturing Network
              </h3>
              <p className="text-secondary text-sm leading-relaxed max-w-xl font-normal">
                Our extensive supplier network spans across India's key industrial corridors, ensuring access to specialized suppliers across major manufacturing hubs.
              </p>
            </div>

            {/* 4 Grid Cards with Spacious Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
              {STAT_CARDS.map((card, idx) => {
                const Icon = card.icon
                return (
                  <motion.div
                    key={card.id}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.06, duration: 0.3 }}
                    className="holographic-theme-card bg-white rounded-xl p-4 md:p-4.5 border border-outline-variant/60 shadow-2xs flex flex-col gap-2 group cursor-default"
                  >
                    {/* Top Row: Icon on left end, Count on right end */}
                    <div className="flex items-center justify-between gap-2 relative z-2">
                      <div className={`size-8.5 rounded-lg flex items-center justify-center ${card.iconBg} transition-transform duration-200 group-hover:scale-105 shrink-0`}>
                        <Icon className="size-4" />
                      </div>
                      <span className="font-sans text-2xl md:text-3xl font-black text-on-surface tracking-tight group-hover:text-primary transition-colors leading-none">
                        <AnimatedCounter targetValue={card.targetNumber} suffix={card.suffix} />
                      </span>
                    </div>

                    {/* Details below row */}
                    <div className="space-y-0.5 relative z-2 pt-1">
                      <h4 className="text-xs md:text-sm font-bold text-on-surface leading-tight">
                        {card.title}
                      </h4>
                      <p className="text-[11px] md:text-xs text-secondary leading-snug font-normal">
                        {card.desc}
                      </p>
                    </div>
                  </motion.div>
                )
              })}
            </div>

          </div>

        </motion.div>

      </div>
    </section>
  )
}
